<template>
    <main id="docs" class="docs-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('docs.title.page') }}</div>
        </portal>

        <header class="docs-hero">
            <div class="docs-hero-icon" aria-hidden="true">
                <i class="fal fa-book"></i>
            </div>
            <div class="docs-hero-body">
                <h1 class="docs-hero-title">{{ $t('docs.title.page') }}</h1>
                <p class="docs-hero-sub">
                    {{ $t('docs.subtitle') || 'Dernières mises à jour et historique des changements.' }}
                </p>
            </div>
        </header>

        <section class="docs-section">
            <div class="docs-section-head">
                <h2 class="docs-section-title">{{ $t('docs.title.versions') }}</h2>
            </div>

            <div v-if="loading" class="docs-state">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Chargement…</span>
                </div>
            </div>

            <div v-else-if="error" class="docs-state docs-state--error">
                <i class="fal fa-exclamation-triangle"></i>
                <p>{{ error }}</p>
            </div>

            <article
                v-else-if="content"
                class="docs-changelog"
                v-html="content"
            ></article>

            <div v-else class="docs-empty">
                <i class="fal fa-cloud-exclamation"></i>
                <p>{{ $t('docs.empty') || 'Aucune donnée disponible pour l\'instant.' }}</p>
            </div>
        </section>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
export default {
    name: 'DocsPage',
    data() {
        return {
            loading: true,
            error: null,
            content: ''
        }
    },
    created() {
        this.loadChangelog()
    },
    methods: {
        async loadChangelog() {
            this.loading = true
            this.error = null
            try {
                const response = await fetch('/CHANGELOG.md', {
                    cache: 'no-cache'
                })
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`)
                }
                this.content = await response.text()
            } catch (err) {
                this.error =
                    this.$t('docs.error') ||
                    'Impossible de charger le journal des changements.'
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<style scoped>
.docs-v2 {
    width: 100%;
    padding: 36px 32px 64px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
    min-height: 100%;
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.docs-hero {
    display: flex;
    align-items: center;
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-6);
}

.docs-hero-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    flex-shrink: 0;
}

.docs-hero-title {
    margin: 0 0 4px;
    font-size: 1.5rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.docs-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 640px;
}

.docs-section {
    margin-bottom: var(--v2-space-8);
}

.docs-section-head {
    display: flex;
    align-items: baseline;
    gap: var(--v2-space-2);
    margin-bottom: var(--v2-space-4);
}

.docs-section-title {
    margin: 0;
    font-size: 0.75rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

/* Changelog card */
.docs-changelog {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    padding: 8px 24px;
    box-shadow: var(--v2-shadow-sm);
}

/* Each <p> in the changelog HTML acts as a release header (date). */
.docs-changelog >>> p {
    position: relative;
    margin: 24px 0 10px;
    padding-left: 18px;
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.docs-changelog >>> p:first-child {
    margin-top: 16px;
}

.docs-changelog >>> p::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--v2-primary);
    box-shadow: 0 0 0 4px var(--v2-primary-soft);
}

/* Vertical connector line between releases */
.docs-changelog >>> p ~ p::after {
    content: '';
    position: absolute;
    left: 3px;
    top: -30px;
    bottom: calc(50% + 10px);
    width: 2px;
    background: var(--v2-border);
}

.docs-changelog >>> ul {
    list-style: none;
    padding: 0 0 0 18px;
    margin: 0 0 14px;
}

.docs-changelog >>> ul:last-child {
    margin-bottom: 16px;
}

.docs-changelog >>> li {
    position: relative;
    padding: 6px 0 6px 18px;
    color: var(--v2-text-2);
    font-size: 0.875rem;
    line-height: 1.55;
}

.docs-changelog >>> li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 14px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--v2-border-strong);
}

.docs-changelog >>> li:hover::before {
    background: var(--v2-primary);
}

.docs-changelog >>> a {
    color: var(--v2-primary);
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color var(--v2-transition-fast);
}

.docs-changelog >>> a:hover {
    border-bottom-color: var(--v2-primary);
}

.docs-changelog >>> strong {
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-semibold);
}

.docs-changelog >>> code {
    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
    font-size: 0.85em;
    padding: 1px 6px;
    border-radius: 4px;
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

/* States */
.docs-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-10, 64px) var(--v2-space-5);
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    color: var(--v2-text-3);
    box-shadow: var(--v2-shadow-sm);
}

.docs-state--error i {
    font-size: 1.75rem;
    color: var(--v2-warning);
}

.docs-state p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

.docs-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-10, 64px) var(--v2-space-5);
    background: var(--v2-surface-0);
    border: 1px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
    color: var(--v2-text-3);
}

.docs-empty i {
    font-size: 2rem;
    color: var(--v2-text-4);
}

.docs-empty p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

@media (max-width: 900px) {
    .docs-v2 {
        padding: 24px 18px 32px;
    }

    .docs-hero-title {
        font-size: 1.25rem;
    }

    .docs-changelog {
        padding: 8px 18px;
    }
}
</style>
